import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, Label, Form
} from "reactstrap";
import { FiDollarSign, FiCreditCard, FiSmartphone, FiRefreshCcw } from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip,
    ResponsiveContainer, Cell
} from "recharts";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const PaymentReportList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [rawResponse, setRawResponse] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });

    const [filters, setFilters] = useState({
        search: "",
        payment_mode: "",
        type: "",
        from_date: dayjs(),
        to_date: dayjs(),
        page: 1,
        limit: 10,
        sort: "DESC",
        filter_period: { value: "today", label: "Today" }
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "year", label: "This Year" },
        { value: "all", label: "All Time" },
        { value: "custom", label: "Custom Range" }
    ];

    const loadReports = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const params = {
                search: currentFilters.search,
                payment_mode: currentFilters.payment_mode,
                type: currentFilters.type,
                page: currentFilters.page,
                limit: currentFilters.limit,
                sort: currentFilters.sort,
                filter: currentFilters.filter_period.value,
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();

            const res = await Handler({ method: "GET", url: `/report/payment_report.php?${queryParams}` });
            if (res?.success) {
                setRawResponse(res);
                setPagination({
                    current_page: res?.current_page || 1,
                    total_pages: res?.total_pages || 1,
                    total_records: res?.total_records || 0
                });
            }
        } catch (err) {
            toast.error("Sync Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter_period.value !== 'custom') {
            loadReports();
        }
    }, [filters.page, filters.sort, filters.payment_mode, filters.type]);

    const handlePeriodChange = (selectedOption) => {
        const newFilters = { ...filters, filter_period: selectedOption, page: 1 };
        setFilters(newFilters);
        if (selectedOption.value !== 'custom') {
            loadReports(newFilters);
        }
    };

    // 🔥 Chart Logic fixed with your exact API keys
    const chartData = useMemo(() => {
        const s = rawResponse?.summary || {};
        return [
            { name: 'Cash', value: Number(s.cash_total || 0), color: '#198754' },
            { name: 'Bank', value: Number(s.bank_total || 0), color: '#0d6efd' },
            { name: 'UPI', value: Number(s.upi_total || 0), color: '#6f42c1' }
        ];
    }, [rawResponse]);

    const reports = rawResponse?.data || [];
    const summary = rawResponse?.summary || {};
    // const pagination = rawResponse?.pagination || {};

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: "10px",
            minHeight: "45px",
            border: "1px solid #dee2e6",
            boxShadow: "none"
        })
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Payment Analytics</h3>
                        <p className="text-muted small m-0">Collection reports by mode and status.</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center mt-3 mt-md-0">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter_period}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3" onClick={() => loadReports()}>
                            <FiRefreshCcw />
                        </Button>
                        <Button color="success" className="px-4 shadow-sm fw-bold rounded-pill" onClick={() => navigate("/payments/add")}>
                            Add Payment
                        </Button>
                    </div>
                </div>

                {/* 🔥 Summary Cards using your exact API response keys */}
                <Row className="g-3 mb-4">
                    <StatCard title="Total Collection" value={`₹${summary.total_amount?.toLocaleString() || 0}`} icon={<FiDollarSign />} color="#333" bg="#fff" />
                    <StatCard title="Cash Total" value={`₹${summary.cash_total?.toLocaleString() || 0}`} icon={<FiDollarSign />} color="#198754" bg="#19875415" />
                    <StatCard title="Bank Total" value={`₹${summary.bank_total?.toLocaleString() || 0}`} icon={<FiCreditCard />} color="#0d6efd" bg="#0d6efd15" />
                    <StatCard title="UPI Total" value={`₹${summary.upi_total?.toLocaleString() || 0}`} icon={<FiSmartphone />} color="#6f42c1" bg="#6f42c115" />
                </Row>

                <Row className="g-4 mb-4">
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4 text-center">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted text-start">Distribution</h6>
                                <div style={{ width: '100%', height: 250 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={chartData} layout="vertical" margin={{ left: -20 }}>
                                            <XAxis type="number" hide />
                                            <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} fontSize={12} />
                                            <Tooltip cursor={{ fill: 'transparent' }} />
                                            <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={30}>
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={8}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted">Advanced Filters</h6>
                                <Form onSubmit={(e) => { e.preventDefault(); loadReports(); }}>
                                    <Row className="g-3 align-items-end">
                                        <Col md={6}>
                                            <Label className="x-small fw-bold">SEARCH CLIENT / INVOICE</Label>
                                            <Input className="bg-light border-0" style={{ height: '45px', borderRadius: '10px' }} placeholder="Type name or invoice #..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
                                        </Col>
                                        <Col md={3}>
                                            <Label className="x-small fw-bold">PAYMENT MODE</Label>
                                            <Input type="select" className="bg-light border-0 text-capitalize" style={{ height: '45px', borderRadius: '10px' }} value={filters.payment_mode} onChange={(e) => setFilters({ ...filters, payment_mode: e.target.value })}>
                                                <option value="">All Modes</option>
                                                <option value="cash">Cash</option>
                                                <option value="bank">Bank</option>
                                                <option value="upi">UPI</option>
                                            </Input>
                                        </Col>
                                        <Col md={3}>
                                            <Label className="x-small fw-bold">ENTRY TYPE</Label>
                                            <Input type="select" className="bg-light border-0 text-capitalize" style={{ height: '45px', borderRadius: '10px' }} value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
                                                <option value="">All Types</option>
                                                <option value="invoice">Against Invoice</option>
                                                <option value="client">Standalone / Advance</option>
                                            </Input>
                                        </Col>
                                        {filters.filter_period.value === 'custom' && (
                                            <>
                                                <Col md={4}>
                                                    <Label className="x-small fw-bold">FROM DATE</Label>
                                                    <DatePicker format="DD/MM/YYYY" value={filters.from_date} onChange={(val) => setFilters({ ...filters, from_date: val })} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                                                </Col>
                                                <Col md={4}>
                                                    <Label className="x-small fw-bold">TO DATE</Label>
                                                    <DatePicker format="DD/MM/YYYY" value={filters.to_date} minDate={filters.from_date} onChange={(val) => setFilters({ ...filters, to_date: val })} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                                                </Col>
                                            </>
                                        )}
                                        <Col md={filters.filter_period.value === 'custom' ? 4 : 12}>
                                            <Button color="primary" block className="fw-bold py-2 shadow-sm" type="submit" style={{ background: '#2e4a3d', border: 'none', height: '45px', borderRadius: '10px' }}>Apply Analytics</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted small text-uppercase ls-1">
                                    <th className="ps-4 py-3">Receipt Info</th>
                                    <th>Method & Type</th>
                                    <th className="text-center">Invoice Summary</th>
                                    <th className="text-end pe-4">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.length > 0 ? (
                                    reports.map((item) => (
                                        <tr key={item.payment_id}>
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold text-dark">{item.client?.name?.toUpperCase()}</div>
                                                <small className="text-muted x-small">{dayjs(item.created_at).format('DD-MM-YYYY hh:mm A')}</small>
                                            </td>
                                            <td>
                                                <Badge color="soft-primary" className="text-primary text-uppercase me-2" style={{ fontSize: '10px' }}>
                                                    {item.payment?.mode}
                                                </Badge>
                                                <Badge color="soft-secondary" className="text-muted text-uppercase" style={{ fontSize: '9px' }}>
                                                    {item.payment?.type}
                                                </Badge>
                                            </td>
                                            <td className="text-center">
                                                {item.invoice ? (
                                                    <div>
                                                        <Badge color="light" className="text-dark border mb-1">#{item.invoice.invoice_no}</Badge>
                                                        {item.invoice_summary && (
                                                            <div className={`x-small fw-bold text-${item.invoice_summary.status === 'paid' ? 'success' : 'danger'}`}>
                                                                BAL: ₹{item.invoice_summary.balance?.toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : <span className="text-muted x-small">N/A</span>}
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="fw-bold text-success fs-6">₹{item.payment?.amount?.toLocaleString()}</div>
                                                {item.payment?.note && <div className="x-small text-muted italic">"{item.payment.note}"</div>}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="4" className="py-5 text-center text-muted italic">No payment records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* 🔥 Updated Pagination with safe checks */}
                {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination.total_pages)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters.page >= pagination.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Col lg={3} md={6}>
        <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <CardBody className="d-flex align-items-center gap-3 p-3">
                <div style={{ width: '45px', height: '45px', borderRadius: '12px', backgroundColor: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                    {icon}
                </div>
                <div>
                    <p className="m-0 text-muted x-small fw-bold text-uppercase">{title}</p>
                    <h5 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h5>
                </div>
            </CardBody>
        </Card>
    </Col>
);

export default PaymentReportList;